import { useEffect, useRef, useState } from 'react';
import { marketingLayout } from '../../theme/marketingTokens';

/** Height of the pinned pipeline stage in its own (unscaled) coordinate space. */
export const PIPELINE_STAGE_HEIGHT = 645;

/**
 * Chrome above the pinned stage: sticky header (76) + wrapper padding (56) +
 * the eyebrow/heading block (~100) + flex gap (20) + bottom padding (24).
 */
const PIPELINE_CHROME = 276;

/** Only re-render the scene when the progress moves by more than this. */
const PROGRESS_EPSILON = 0.004;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Scale that fits the 645px stage into the pinned viewport.
 * Recomputed on scroll and resize, per the handoff.
 */
export const pipelineStageScale = (viewportHeight: number): number =>
  clamp((viewportHeight - PIPELINE_CHROME) / 650, 0.42, 1.9);

export interface MarketingScrollState {
  /** 0–1 progress through the pinned pipeline scene. */
  pipelineProgress: number;
  /** 0–1 eased progress of the stat count-up, once triggered. */
  statsProgress: number;
  /** Current stage scale; drives both the stage transform and its sizing box. */
  stageScale: number;
  /** 0–1 progress through the About timeline reveal. */
  timelineProgress: number;
}

export interface MarketingScrollTargets {
  /** The 230vh pinned pipeline section (Home). */
  pipeline?: React.RefObject<HTMLElement | null>;
  /** The row of stat counters (Home or About). */
  stats?: React.RefObject<HTMLElement | null>;
  /** The scroll-revealed journey timeline (About). */
  timeline?: React.RefObject<HTMLElement | null>;
}

/**
 * Drives the home page's scroll-linked motion from a single rAF loop.
 *
 * It reads `getBoundingClientRect()` every frame rather than listening for
 * `scroll`. That is deliberate: if the page ever scrolls inside a container
 * rather than the window — as it does in any shell with its own scroll region —
 * scroll events fire on that container and never reach `window`, and every
 * scroll-derived value silently stays at 0.
 *
 * A missing target is skipped rather than early-returning, so a page without a
 * pipeline section still gets its stat counters.
 */
export const useMarketingScroll = (
  targets: MarketingScrollTargets
): MarketingScrollState => {
  const { pipeline: pipelineRef, stats: statsRef, timeline: timelineRef } = targets;
  const [state, setState] = useState<MarketingScrollState>(() => ({
    pipelineProgress: 0,
    statsProgress: 0,
    timelineProgress: 0,
    stageScale: pipelineStageScale(
      typeof window === 'undefined' ? 900 : window.innerHeight
    ),
  }));

  // Read inside the loop without making it a dependency.
  const stateRef = useRef(state);
  stateRef.current = state;
  const statsStarted = useRef(false);

  useEffect(() => {
    const reduced = prefersReducedMotion();

    if (reduced) {
      // No scroll-linked motion: show both the scene and the counters resolved.
      setState((prev) => ({
        ...prev,
        pipelineProgress: 1,
        statsProgress: 1,
        timelineProgress: 1,
      }));
      return;
    }

    let frame = 0;
    let alive = true;

    const countUpStats = () => {
      if (statsStarted.current) return;
      statsStarted.current = true;
      const start = performance.now();
      const duration = 1400;
      const step = (now: number) => {
        if (!alive) return;
        const t = Math.min(1, (now - start) / duration);
        setState((prev) => ({ ...prev, statsProgress: 1 - Math.pow(1 - t, 3) }));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const tick = () => {
      if (!alive) return;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const pipelineEl = pipelineRef?.current;
      if (pipelineEl) {
        const rect = pipelineEl.getBoundingClientRect();
        const travel = rect.height - (viewportHeight - marketingLayout.headerHeight);
        const progress =
          travel > 0
            ? clamp((marketingLayout.headerHeight - rect.top) / travel, 0, 1)
            : 0;
        const scale = pipelineStageScale(viewportHeight);
        const prev = stateRef.current;
        if (
          Math.abs(progress - prev.pipelineProgress) > PROGRESS_EPSILON ||
          Math.abs(scale - prev.stageScale) > 0.001
        ) {
          setState((current) => ({
            ...current,
            pipelineProgress: progress,
            stageScale: scale,
          }));
        }
      }

      const statsEl = statsRef?.current;
      if (statsEl && statsEl.getBoundingClientRect().top < viewportHeight * 0.85) {
        countUpStats();
      }

      const timelineEl = timelineRef?.current;
      if (timelineEl) {
        const rect = timelineEl.getBoundingClientRect();
        const progress = clamp(
          (viewportHeight * 0.85 - rect.top) / (rect.height * 0.75),
          0,
          1
        );
        // Coarser threshold than the pipeline: rows reveal in steps, so
        // sub-pixel changes cannot show.
        if (Math.abs(progress - stateRef.current.timelineProgress) > 0.008) {
          setState((current) => ({ ...current, timelineProgress: progress }));
        }
      }

      frame = requestAnimationFrame(tick);
    };

    // The loop reads getBoundingClientRect() every frame, so leaving it running
    // for the whole page would burn battery on a mostly-idle marketing page.
    // An IntersectionObserver starts it only while a target is near the
    // viewport and stops it again once they all leave. The margin gives the
    // loop a head start so the first painted frame is already correct.
    const targets = [pipelineRef?.current, statsRef?.current, timelineRef?.current]
      .filter((el): el is HTMLElement => Boolean(el));

    if (targets.length === 0) return;

    let visible = 0;

    const start = () => {
      if (frame === 0) frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (frame !== 0) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
    };

    // Without IntersectionObserver, fall back to the always-on loop.
    if (typeof IntersectionObserver === 'undefined') {
      start();
      return () => {
        alive = false;
        stop();
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visible += entry.isIntersecting ? 1 : -1;
        }
        visible = Math.max(0, visible);
        if (visible > 0) start();
        else stop();
      },
      { rootMargin: '200px 0px' }
    );
    targets.forEach((el) => observer.observe(el));

    return () => {
      alive = false;
      observer.disconnect();
      stop();
    };
  }, [pipelineRef, statsRef, timelineRef]);

  return state;
};
